import { useEffect, useRef, useState } from "react";

import { carryOverForAdmission } from "./emergency-billing-source";
import { isEmergencySubject, subjectIdOf } from "./billing-subject";

/**
 * When a billing screen opens an admission, move the ED charges of the visit
 * the patient was admitted from onto its bill. The move is idempotent on the
 * server, so running it on every open is safe and is how an admission created
 * after the ED visit closed picks its charges up.
 *
 * Calls `onMoved` when something moved, so the screen re-reads its charges.
 * Returns the reason a move failed ("" when nothing failed); the screen shows
 * it as a banner. A lookup that could not run is not shown — the next open
 * tries again.
 */
export default function useEdCarryOver(patient, onMoved) {
  const [error, setError] = useState("");
  const onMovedRef = useRef(onMoved);
  onMovedRef.current = onMoved;

  const admissionId = isEmergencySubject(patient) ? null : subjectIdOf(patient);

  useEffect(() => {
    setError("");
    if (!admissionId) return undefined;
    let cancelled = false;
    carryOverForAdmission(admissionId)
      .then(({ moved, failed }) => {
        if (cancelled) return;
        if (failed.length) {
          setError(`Some emergency charges could not be moved onto this bill: ${failed.join("; ")}`);
        }
        if (moved) onMovedRef.current?.();
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [admissionId]);

  return error;
}
